import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldOff, Activity, AlertOctagon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

const ACTION_LABELS = {
  "auth.login": "Inicio de sesión",
  "auth.login_failed": "Intento de inicio de sesión fallido",
  "auth.2fa_challenge": "Reto 2FA enviado",
  "auth.2fa_enabled": "2FA activado",
  "auth.2fa_disabled": "2FA desactivado",
  "auth.password_reset_requested": "Recuperación de contraseña solicitada",
  "auth.password_reset": "Contraseña restablecida",
  "company.registered": "Empresa registrada",
  "company.created_by_platform": "Empresa creada por la plataforma",
  "auth.invitation_activated": "Cuenta activada por invitación",
  "machine.created": "Máquina creada",
  "machine.updated": "Máquina editada",
  "machine.deleted": "Máquina eliminada",
  "document.uploaded": "Documento subido",
  "document.updated": "Documento editado",
  "document.deleted": "Documento eliminado",
  "location.created": "Ubicación creada",
  "location.updated": "Ubicación editada",
  "location.deleted": "Ubicación eliminada",
  "user.created": "Usuario creado",
  "user.updated": "Usuario editado",
  "user.deleted": "Usuario eliminado",
  "intervention.created": "Intervención registrada",
  "intervention.deleted": "Intervención eliminada",
  "photos.uploaded": "Fotos subidas",
  "photo.deleted": "Foto eliminada",
};

const fmtDate = (d) => {
  try {
    return format(new Date(d), "d MMM yyyy · HH:mm", { locale: es });
  } catch {
    return d;
  }
};

function Card({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <section className="bg-white rounded-2xl border border-zinc-200/80 card-shadow p-6 sm:p-8" data-testid={testid}>
      <div className="flex items-start gap-4 mb-6">
        <span className="h-11 w-11 rounded-xl bg-zinc-100 flex items-center justify-center text-zinc-600 shrink-0">
          <Icon size={19} />
        </span>
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">{title}</h2>
          <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const totpEnabled = user?.totp_enabled;

  useEffect(() => {
    api.get("/audit-logs").then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  }, []);

  const begin2fa = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      setCode("");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const enable2fa = async () => {
    setSaving(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Verificación en dos pasos activada");
      setSetup(null);
      window.location.reload();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const disable2fa = async () => {
    setSaving(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("Verificación en dos pasos desactivada");
      setDisableOpen(false);
      window.location.reload();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteCompany = async () => {
    setSaving(true);
    try {
      await api.delete("/company", { data: { password } });
      toast.success("Empresa y datos eliminados");
      await logout().catch(() => {});
      navigate("/");
    } catch (e) {
      toast.error(fmtErr(e));
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-up" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter">Configuración</h1>
        <p className="text-zinc-500 text-sm mt-1">{user?.company_name}</p>
      </div>

      <Card
        icon={totpEnabled ? ShieldCheck : ShieldOff}
        title="Verificación en dos pasos (2FA)"
        subtitle={totpEnabled
          ? "Activada. Tu cuenta pide un código adicional al iniciar sesión."
          : "Desactivada. Recomendado para administradores."}
        testid="security-2fa-card"
      >
        {totpEnabled ? (
          <div className="flex items-center justify-between gap-4">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200/70" data-testid="2fa-status-enabled">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Activada
            </span>
            <button
              onClick={() => { setDisableOpen(true); setCode(""); }}
              data-testid="2fa-disable-button"
              className="px-5 py-2.5 rounded-full border border-zinc-200 text-sm font-medium hover:border-zinc-300 transition-colors"
            >
              Desactivar
            </button>
          </div>
        ) : setup ? (
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Escanea este QR con Google Authenticator (o cualquier app TOTP) y escribe el código de
              6 dígitos para confirmar.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <img src={setup.qr_data_url} alt="QR de configuración 2FA" className="h-44 w-44 rounded-2xl border border-zinc-200" data-testid="2fa-qr-image" />
              <div className="w-full space-y-3">
                <input
                  className={`${inputCls} text-center text-xl tracking-[0.4em] font-mono`}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="······"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  data-testid="2fa-enable-code-input"
                />
                <button
                  onClick={enable2fa}
                  disabled={saving || code.length !== 6}
                  data-testid="2fa-enable-submit"
                  className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 disabled:opacity-50 transition-colors"
                >
                  {saving ? "Verificando…" : "Verificar y activar"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={begin2fa}
            disabled={saving}
            data-testid="2fa-setup-button"
            className="px-5 py-3 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200 disabled:opacity-50"
          >
            {saving ? "Generando…" : "Activar 2FA"}
          </button>
        )}
      </Card>

      <Card icon={Activity} title="Actividad" subtitle="Registro interno e inmutable de acciones relevantes de tu empresa." testid="audit-log-card">
        {logs === null ? (
          <div className="flex justify-center py-8">
            <div className="h-7 w-7 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-zinc-400" data-testid="audit-empty">Todavía no hay actividad registrada.</p>
        ) : (
          <div className="space-y-1 max-h-[420px] overflow-y-auto -mr-2 pr-2" data-testid="audit-log-list">
            {logs.map((log) => (
              <div key={log.id} className="flex items-start justify-between gap-4 py-2.5 border-b border-zinc-50 last:border-0" data-testid={`audit-entry-${log.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900">
                    {ACTION_LABELS[log.action] || log.action}
                    {log.detail && <span className="text-zinc-500 font-normal"> · {log.detail}</span>}
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {log.user_name || "Sistema"}{log.ip ? ` · IP ${log.ip}` : ""}
                  </p>
                </div>
                <p className="text-xs text-zinc-400 whitespace-nowrap shrink-0 capitalize">{fmtDate(log.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card icon={AlertOctagon} title="Zona de peligro" subtitle="Acciones irreversibles sobre los datos de tu empresa." testid="danger-zone-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-rose-200 bg-rose-50/50 p-5">
          <div>
            <p className="font-medium text-sm text-zinc-900">Eliminar empresa y todos sus datos</p>
            <p className="text-sm text-zinc-500 mt-1">
              Se eliminan usuarios, máquinas, ubicaciones, documentos, fotos, historial y registro de actividad. Irreversible.
            </p>
          </div>
          <button
            onClick={() => { setDeleteOpen(true); setPassword(""); }}
            data-testid="delete-company-button"
            className="shrink-0 px-5 py-2.5 rounded-full bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 active:scale-95 transition-[background-color,transform] duration-200"
          >
            Eliminar empresa
          </button>
        </div>
      </Card>

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="rounded-3xl sm:max-w-sm" data-testid="2fa-disable-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Desactivar 2FA</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-zinc-500">Introduce el código actual de tu aplicación de autenticación.</p>
            <input
              className={`${inputCls} text-center text-xl tracking-[0.4em] font-mono`}
              inputMode="numeric"
              maxLength={6}
              placeholder="······"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="2fa-disable-code-input"
            />
            <button
              onClick={disable2fa}
              disabled={saving || code.length !== 6}
              data-testid="2fa-disable-submit"
              className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 disabled:opacity-50 transition-colors"
            >
              {saving ? "Verificando…" : "Desactivar 2FA"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded-3xl sm:max-w-sm" data-testid="delete-company-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Eliminar empresa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-zinc-500">
              Esta acción elimina permanentemente <strong>{user?.company_name}</strong> y todos sus
              datos. Escribe tu contraseña para confirmar.
            </p>
            <input
              className={inputCls}
              type="password"
              placeholder="Tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="delete-company-password-input"
            />
            <button
              onClick={deleteCompany}
              disabled={saving || !password}
              data-testid="delete-company-confirm"
              className="w-full h-12 rounded-full bg-rose-600 text-white font-medium hover:bg-rose-700 disabled:opacity-50 transition-colors"
            >
              {saving ? "Eliminando…" : "Eliminar todo permanentemente"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
